import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, GitBranch, ChevronRight, Play, Zap, CheckCircle2, AlertCircle } from "lucide-react";
import { relativeTime } from "@/lib/utils";
import { toast } from "sonner";

export default function WorkflowsPage() {
  const [wfs, setWfs] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [inputs, setInputs] = useState({});
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const load = async () => {
    const [a, r] = await Promise.all([api.get("/workflows"), api.get("/workflows/runs?limit=10")]);
    setWfs(a.data.workflows || []);
    setRuns(r.data.runs || []);
  };
  useEffect(() => { load(); }, []);

  const pick = (wf) => {
    setSelected(wf);
    setLast(null);
    setInputs(Object.fromEntries((wf.inputs || []).map((k) => [k, ""])));
  };

  const run = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post("/workflows/run", { workflow_id: selected.id, inputs });
      setLast(data);
      if (data.action?.summary) toast.success(data.action.summary);
      else toast.success(`Workflow "${selected.name}" ${data.status}`);
      load();
    } catch (e) { toast.error("Workflow failed"); }
    setBusy(false);
  };

  return (
    <DashboardLayout eyebrow="Multi-agent orchestration" title="Workflows">
      <div className="grid lg:grid-cols-12 gap-6">
        {/* Workflow list */}
        <div className="lg:col-span-5 space-y-3">
          <div className="label-eyebrow mb-1">Available workflows</div>
          {wfs.map((wf, i) => (
            <button key={wf.id} onClick={() => pick(wf)} data-testid={`workflow-${wf.id}`}
              className={`w-full text-left data-card data-card-hover ${selected?.id === wf.id ? "border-foreground/40" : ""}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <GitBranch className="w-4 h-4" />
                  <span className="font-display font-semibold tracking-tight">{wf.name}</span>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{wf.description}</p>
              {wf.outcome && (
                <div className="mt-2 flex items-start gap-1.5 text-[0.7rem] text-signal-500">
                  <Zap className="w-3 h-3 mt-0.5 flex-shrink-0" /><span>{wf.outcome}</span>
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {wf.steps.map((s, j) => (
                  <span key={j} className="signal-pill">{s}</span>
                ))}
              </div>
            </button>
          ))}
        </div>

        {/* Runner + activity */}
        <div className="lg:col-span-7 space-y-6">
          {selected ? (
            <div className="data-card">
              <div className="label-eyebrow mb-1">Run workflow</div>
              <h3 className="font-display font-semibold text-lg">{selected.name}</h3>
              <p className="text-xs text-muted-foreground mb-4">{selected.description}</p>
              {selected.outcome && (
                <div className="flex items-start gap-1.5 text-xs text-signal-500 mb-4 -mt-2">
                  <Zap className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span><span className="font-medium">What this does:</span> {selected.outcome}</span>
                </div>
              )}
              <div className="space-y-3">
                {(selected.inputs || []).map((k) => (
                  <div key={k}>
                    <Label className="text-xs capitalize">{k.replace(/_/g, " ")}</Label>
                    <Input data-testid={`workflow-input-${k}`} value={inputs[k] || ""} onChange={(e) => setInputs({ ...inputs, [k]: e.target.value })} />
                  </div>
                ))}
                {(selected.inputs || []).length === 0 && <p className="text-xs text-muted-foreground">No inputs required — analyses your workspace data directly.</p>}
              </div>
              <div className="mt-4 flex justify-end">
                <Button onClick={run} disabled={busy} data-testid="workflow-run-btn" className="rounded-sm">
                  <Play className="w-3.5 h-3.5 mr-1.5" />{busy ? "Running..." : "Execute workflow"}
                </Button>
              </div>
              {last && (
                <div className="mt-5 border-t border-border pt-4">
                  {last.action && (
                    <div className={`mb-4 p-3 rounded-sm border flex items-start gap-2 ${last.action.done ? "border-signal-500/40 bg-signal-500/5" : "border-amber-accent/40 bg-amber-accent/5"}`} data-testid="workflow-action-summary">
                      {last.action.done ? <CheckCircle2 className="w-4 h-4 text-signal-500 mt-0.5 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 text-amber-accent mt-0.5 flex-shrink-0" />}
                      <div>
                        <div className="text-xs font-medium">{last.action.done ? "Action completed" : "Needs attention"}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{last.action.summary}</div>
                      </div>
                    </div>
                  )}
                  <div className="label-eyebrow mb-2">AI output</div>
                  <div className="flex items-center gap-2 mb-3">
                    {last.steps.map((s, i) => (
                      <span key={i} className={`signal-pill ${s.status === "success" ? "text-signal-500 border-signal-500/40" : "text-destructive border-destructive/40"}`}>
                        {s.agent_type}: {s.status}
                      </span>
                    ))}
                  </div>
                  <pre className="text-xs font-mono whitespace-pre-wrap bg-muted/40 border border-border rounded-sm p-3 max-h-80 overflow-y-auto" data-testid="workflow-output">{JSON.stringify(last.state, null, 2)}</pre>
                </div>
              )}
            </div>
          ) : (
            <div className="data-card text-sm text-muted-foreground text-center py-10">Pick a workflow on the left to run it.</div>
          )}

          <div className="data-card">
            <div className="label-eyebrow mb-2">Recent runs</div>
            <ul className="space-y-2.5">
              {runs.length === 0 && <li className="text-xs text-muted-foreground">No workflow runs yet.</li>}
              {runs.map((r, i) => (
                <li key={r.id} className="flex items-center justify-between border-l-2 border-signal-500/40 pl-3 py-1" data-testid={`workflow-run-${i}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{r.workflow_name}</div>
                    <div className="text-[0.7rem] text-muted-foreground">{relativeTime(r.started_at)} · {r.steps?.length || 0} steps</div>
                  </div>
                  <span className={`text-[0.65rem] tracking-widest uppercase ${r.status === "success" ? "text-signal-500" : "text-amber-accent"}`}>{r.status}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
